import cn from 'classnames';
import Section from 'react-scale/components/elements/section';
import SectionTitle from 'react-scale/components/elements/section-title';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { WheelControls } from 'react-scale/lib/keen-slider';

const Resources: FC<{ resources: { resource: Resource }[] }> = ({ resources }) => {
  const sliderParentRef = useRef<HTMLDivElement>();
  const [loaded, setLoaded] = useState(false);
  const [sliderRef, slider] = useKeenSlider(
    {
      initial: 0,
      mode: 'snap',
      rubberband: false,
      slides: {
        perView: 1.05,
        spacing: 24
      },
      breakpoints: {
        '(min-width: 640px)': {
          mode: 'free',
          slides: {
            perView: 1.4,
            spacing: 24
          }
        },
        '(min-width: 768px)': {
          mode: 'free',
          slides: {
            perView: 2,
            spacing: 24
          }
        },
        '(min-width: 1280px)': {
          mode: 'free',
          slides: {
            perView: 3,
            spacing: 24
          }
        }
      },
      dragStarted: () => (slider.current.container.style.cursor = 'grabbing'),
      dragEnded: () => (slider.current.container.style.cursor = 'grab'),
      created: () => setLoaded(true)
    },
    [WheelControls]
  );

  useEffect(() => {
    if (!sliderParentRef.current) return;

    const handleTab = (e: KeyboardEvent): void => {
      if (e.key === 'Tab') {
        if (e.shiftKey) slider.current.prev();
        else slider.current.next();
      }
    };
    const handleFocus = (e: FocusEvent): void => {
      const targetIsFirst = e.target === slider.current.slides[0];
      const targetIsLast = e.target === slider.current.slides[slider.current.slides.length - 1];

      if (targetIsFirst) slider.current.moveToIdx(slider.current.track.details.minIdx);
      if (targetIsLast) slider.current.moveToIdx(slider.current.track.details.maxIdx);
    };

    sliderParentRef.current.addEventListener('keydown', handleTab);
    sliderParentRef.current.addEventListener('focusin', handleFocus);

    return () => {
      if (sliderParentRef.current) {
        sliderParentRef.current.removeEventListener('keydown', handleTab);
        sliderParentRef.current.removeEventListener('focusin', handleFocus);
      }
    };
  }, []);

  return (
    <Section overflowHidden>
      {/* @ts-ignore: Unreachable code error */}
      <SectionTitle
        overtitleClassName="text-gradient-pink"
        overtitle="Learn How We Serve Enterprises Large and Small"
        title="Case Studies & Resources"
        subtitle="We've worked with the world's leading AI teams for years, and delivered more high-quality data than anybody else."
      />

      <div ref={sliderParentRef}>
        <div
          ref={sliderRef}
          className={cn(
            'keen-slider !overflow-visible cursor-grab transition-opacity duration-300 h-[417px] md:h-[375px] lg:h-[414px] xl:h-[401px]',
            loaded ? 'opacity-100 visible' : 'opacity-0 invisible'
          )}
        >
          {resources.map(({ resource }, index) => {
            return (
              <ResourceCard
                key={index}
                value={{
                  data: {
                    overtitle: resource?.value.data.overtitle,
                    title: resource?.value.data.title,
                    image: resource?.value.data.image,
                    link: resource?.value.data.link
                  }
                }}
              />
            );
          })}
        </div>
      </div>
    </Section>
  );
};

export type Resource = {
  value: {
    data: {
      overtitle: string;
      title: string;
      image: { src: string; alt: string };
      link: string;
    };
  };
};

const ResourceCard: FC<Resource> = ({
  value: {
    data: { overtitle, title, image, link }
  }
}) => {
  return (
    <Link href={link} className="rounded-lg keen-slider__slide group">
      <div className="aspect-[389/243] rounded-lg overflow-hidden">
        <Image
          src={image?.src}
          width={382}
          height={240}
          alt={image?.alt}
          quality={100}
          className="object-cover w-full h-full transition-transform duration-700 ease-in-out rounded-lg group-hover:scale-110"
        />
      </div>
      <div className="pt-6">
        <p className="mb-1 text-sm text-scale-lightmauve">{overtitle}</p>
        <p className="text-2xl font-aeonik">{title}</p>
      </div>
    </Link>
  );
};

export default Resources;
